import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkBreaks from "remark-breaks";
import axios from "axios";
import { useAeraWebSocket } from "@/hooks/useAeraWebSocket";
import { useSetup } from "@/hooks/useSetup";
import ProcessingIndicator from "./ProcessingIndicator";
import ScrollToBottomButton from "./ScrollToBottomButton";
import { AeraButton } from "@/app/components/ui/AeraButton";
import { AeraImage } from "@/app/components/ui/AeraImage";
import { ChatInput } from "./chat/ChatInput";
import { SelectedFilesStrip } from "./chat/SelectedFilesStrip";
import { SuggestedActions } from "./chat/SuggestedActions";

interface ChatMessage {
  id?: string;
  role: "user" | "ai";
  text: string;
  isStreaming?: boolean;
  isEnding?: boolean;
  apiPath?: string;
}

interface AgentChatPanelProps {
  deployId: string;
  workerName?: string;
  suggested_actions?: boolean;
  isOnboarding?: boolean;
  steps?: Array<{ step_title?: string; title?: string; config?: unknown }>;
  userId?: string;
  orderId?: string;
  productId?: string;
  /** When true, parent has resolved deploy_id (e.g. order loaded). */
  deployIdResolved?: boolean;
  /** When true, parent has loaded the order and confirmed it has no taskId. We only start deploy/init when this is true (when we have no deploy_id). */
  orderHasNoTaskId?: boolean;
  /** Notify parent when setup/init has fully succeeded. */
  onInstalled?: () => void;
  /** Bumped when a sibling panel resizes (e.g. FirstResponder right panel drag). */
  layoutResizeKey?: number;
  onContentScrollStateChange?: (isScrolled: boolean) => void;
}

// Simple icon component (Matched with ChatPanel)
const Icon = ({ name, size = 16 }: { name: string; size?: number }) => {
  const icons: Record<string, React.ReactNode> = {
    link: (
      <>
        <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
        <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
      </>
    ),
    file: (
      <>
        <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" />
        <polyline points="14 2 14 8 20 8" />
      </>
    ),
    hourglass: (
      <>
        <path d="M5 22h14" />
        <path d="M5 2h14" />
        <path d="M17 22v-4.172a2 2 0 0 0-.586-1.414L12 12l4.414-4.414A2 2 0 0 0 17 6.172V2" />
        <path d="M7 22v-4.172a2 2 0 0 1 .586-1.414L12 12 7.586 7.586A2 2 0 0 1 7 6.172V2" />
      </>
    ),
  };

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {icons[name]}
    </svg>
  );
};

const parseMessage = (content: string) => {
  // Replace escaped newlines with actual newlines
  content = content.replace(/\\n/g, "\n");
  // Replace \n\n with \n + zero-width space + \n so remark-breaks produces <br><br>
  content = content.replace(/\n\n/g, "\n\u200B\n");
  // Escape "N)" at start of lines to prevent Markdown from creating <ol><li>
  content = content.replace(/(^|\n)(\d+)\) /g, "$1$2\\) ");
  const textLines: string[] = [];
  const attachmentLines: string[] = [];
  let suggestSection: {
    screen_title?: string;
    layout?: string;
    elements?: Array<{
      type?: string;
      data?: { label?: string };
      [key: string]: unknown;
    }>;
  } | null = null;
  let hasLoadingSpinner = false;

  // Check for LOADING_SPINNER marker
  if (content.includes("[[LOADING_SPINNER]]")) {
    hasLoadingSpinner = true;
    content = content.replace(/\[\[LOADING_SPINNER\]\]/g, "").trim();
  }

  // Check for SUGGEST_SECTION - handles inline JSON with nested objects
  const suggestMatch = content.match(/\[\[SUGGEST_SECTION\]\]([\s\S]*)$/);
  if (suggestMatch) {
    let jsonStr = suggestMatch[1].trim();

    // Always hide the raw text from user even if it's incomplete
    content = content.replace(/\[\[SUGGEST_SECTION\]\][\s\S]*$/, "").trim();
    // Remove trailing zero-width space sequences
    content = content.replace(/(\n\u200B|\n)+$/, "").trim();

    try {
      // Extract just the JSON block by finding the outermost braces
      const startIdx = jsonStr.indexOf("{");
      const endIdx = jsonStr.lastIndexOf("}");
      if (startIdx !== -1 && endIdx !== -1 && endIdx > startIdx) {
        jsonStr = jsonStr.substring(startIdx, endIdx + 1);
      }
      suggestSection = JSON.parse(jsonStr);
    } catch {
      // Silently fail during streaming as the JSON string is often incomplete
    }
  }

  // Extract file attachments with pattern [filename]
  // Match [filename.ext] but not [[SUGGEST_SECTION]]
  const filePattern = /\[([^\[\]]+\.[a-zA-Z0-9]+)\]/g;
  const files: string[] = [];
  let match;

  while ((match = filePattern.exec(content)) !== null) {
    files.push(match[1]); // Extract filename without brackets
  }

  if (files.length > 0) {
    // Remove file references from content
    content = content.replace(filePattern, "").trim();
    // Add files as attachment lines
    files.forEach((filename) => {
      attachmentLines.push(`[${filename}]`);
    });
  }

  // Also support old markdown link format [text](url) for backward compatibility
  const linkLineRegex = /^(\s*\[[^\]]+\]\([^\)]+\)\s*)+$/;
  const lines = content.split("\n");

  lines.forEach((line) => {
    if (linkLineRegex.test(line)) {
      attachmentLines.push(line);
    } else if (line.trim()) {
      textLines.push(line);
    }
  });

  return {
    text: textLines
      .join("\n")
      .replace(/(\n\u200B|\n)+$/, "")
      .trim(),
    attachments: attachmentLines.join("\n").trim(),
    suggestSection,
    hasLoadingSpinner,
  };
};

// Component for displaying attached files (Images & Docs)
const InlineAttachment = ({
  filename,
  getFileUrl,
  handlePreviewFile,
}: {
  filename: string;
  getFileUrl: (filename: string) => Promise<string | null>;
  handlePreviewFile: (file: {
    name: string;
    url: string;
    type: string;
    size?: number;
  }) => void;
}) => {
  const [fileUrl, setFileUrl] = useState<string | null>(null);
  const fileExt = filename.split(".").pop()?.toLowerCase() || "";
  const isImage = ["jpg", "jpeg", "png", "gif", "webp", "bmp", "svg"].includes(
    fileExt,
  );

  useEffect(() => {
    getFileUrl(filename)
      .then((url) => {
        setFileUrl(url);
      })
      .catch((err) => {
        console.error("InlineAttachment: Error fetching URL", err);
      });
  }, [filename, getFileUrl]);

  if (!fileUrl) {
    return (
      <div className="flex items-center gap-2 text-sm text-aera-charcoal my-2 p-2 bg-gray-50 rounded-lg animate-pulse w-fit">
        <div className="animate-spin h-3 w-3 border-2 border-aera-charcoal border-t-transparent rounded-full"></div>
        Loading {filename}...
      </div>
    );
  }

  if (isImage) {
    return (
      <AeraImage
        native
        src={fileUrl}
        alt={filename}
        className="max-w-full rounded-lg my-2 cursor-pointer hover:opacity-90 transition-opacity border border-gray-200"
        style={{ maxHeight: "300px" }}
        onClick={() =>
          handlePreviewFile({
            name: filename,
            url: fileUrl,
            type: fileExt,
            size: 0,
          })
        }
      />
    );
  }

  // Document / Generic File
  return (
    <div
      className="flex items-center gap-3 p-3 my-2 bg-aera-white border border-gray-200 rounded-xl cursor-pointer hover:bg-gray-50 transition-colors w-fit max-w-full shadow-sm"
      onClick={() =>
        handlePreviewFile({
          name: filename,
          url: fileUrl,
          type: fileExt,
          size: 0,
        })
      }
    >
      <div className="p-2 bg-aera-amberlight/20 rounded-lg text-aera-amber">
        <Icon name="file" size={20} />
      </div>
      <div className="flex flex-col min-w-0">
        <span className="text-sm font-medium text-aera-slate truncate max-w-[200px]">
          {filename}
        </span>
        <span className="text-xs text-aera-primary font-medium hover:underline">
          Click to view
        </span>
      </div>
    </div>
  );
};

export default function AgentChatPanel({
  deployId,
  workerName = "AI Agent",
  suggested_actions = true,
  isOnboarding = false,
  steps = [],
  userId,
  orderId,
  productId,
  deployIdResolved = true,
  orderHasNoTaskId = false,
  onInstalled,
  layoutResizeKey,
  onContentScrollStateChange,
}: AgentChatPanelProps) {
  const [chatHistory, setChatHistory] = useState<ChatMessage[]>([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [hasNoHistory, setHasNoHistory] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const chatEndRef = useRef<HTMLDivElement>(null);
  const chatScrollRef = useRef<HTMLDivElement>(null);
  const chatSectionRef = useRef<HTMLDivElement>(null);
  const [sectionWidth, setSectionWidth] = useState(0);
  const hasInitialScrolled = useRef(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const isLoadingRef = useRef(false);
  const [fileList, setFileList] = useState<
    Array<{ filename?: string; shared_link?: string }>
  >([]);
  // Pending greeting state for pre-fetching
  const [pendingGreeting, setPendingGreeting] = useState<ChatMessage | null>(
    null,
  );
  const [isGreetingLoaded, setIsGreetingLoaded] = useState<boolean | null>(
    null,
  );

  const {
    localSetupStep,
    shouldShowSetup,
    setupStatusText,
    initWsSuccess,
    setIsInitRequestDone,
    hadOnboarding,
    showSetupComplete,
    triggerAutoRetry,
    runInitFromWsDeployId,
    effectiveDeployId,
    orderData,
  } = useSetup({
    deployId,
    orderId,
    productId,
    steps,
    isOnboarding,
    deployIdResolved,
    orderHasNoTaskId,
  });

  const hasNotifiedInstalledRef = useRef(false);
  useEffect(() => {
    if (initWsSuccess && !hasNotifiedInstalledRef.current) {
      hasNotifiedInstalledRef.current = true;
      onInstalled?.();
    }
  }, [initWsSuccess, onInstalled]);

  const hasActiveStream = useMemo(
    () => chatHistory.some((m) => m.isStreaming),
    [chatHistory],
  );

  const effectiveSetupStatusText = useMemo(() => {
    // If setup complete message would show on refresh, prefer showing "loading history" ONLY while history is loading.
    // Once history has loaded (and exists), hide the setup-complete processing message entirely.
    if (shouldShowSetup && setupStatusText === "✅ ติดตั้งสำเร็จแล้ว") {
      // If AI is currently streaming, don't show setup text at the same time.
      if (hasActiveStream) return undefined;
      if (isLoadingHistory) return "กำลังโหลดประวัติแชท";
      if (!hasNoHistory) return undefined;
    }
    return setupStatusText;
  }, [
    shouldShowSetup,
    setupStatusText,
    isLoadingHistory,
    hasNoHistory,
    hasActiveStream,
  ]);

  // Component-scoped dedup: skip duplicate WS messages within this instance
  // Map of request_id → api type owned by this component instance
  const ownedRequestsRef = useRef<Map<string, "greeting" | "agent_chat">>(
    new Map(),
  );
  // Dedup: prevent React StrictMode double-invoke from firing same WS event twice
  const lastProcessedMsgKeyRef = useRef("");
  // Buffer WS messages that arrive before request_id is registered (e.g. fast backend responses)
  const pendingWsByRequestIdRef = useRef<
    Map<
      string,
      {
        streamText?: string;
        isEnd?: boolean;
        publishText?: string;
        apiPath?: string;
      }
    >
  >(new Map());

  const markdownComponents = useMemo<
    Parameters<typeof ReactMarkdown>[0]["components"]
  >(
    () => ({
      h1: "p",
      h2: "p",
      h3: "p",
      h4: "p",
      h5: "p",
      h6: "p",
      a: ({ ...props }) => (
        <AeraButton
          as="a"
          {...props}
          variant="link"
          textColor="amberdark"
          target="_blank"
          rel="noopener noreferrer"
        />
      ),
      img: ({ ...props }) => {
        if (props.alt === "cursor") {
          return (
            <span className="inline-flex items-center gap-0.5 ml-1 align-middle">
              <span
                className="w-1.5 h-1.5 bg-aera-amber rounded-full animate-bounce"
                style={{ animationDelay: "0ms", animationDuration: "1s" }}
              ></span>
              <span
                className="w-1.5 h-1.5 bg-aera-amber rounded-full animate-bounce"
                style={{ animationDelay: "150ms", animationDuration: "1s" }}
              ></span>
              <span
                className="w-1.5 h-1.5 bg-aera-amber rounded-full animate-bounce"
                style={{ animationDelay: "300ms", animationDuration: "1s" }}
              ></span>
            </span>
          );
        }
        return (
          <AeraImage
            native
            src={typeof props.src === "string" ? props.src : ""}
            alt={props.alt ?? ""}
            className="max-w-full rounded-lg"
          />
        );
      },
    }),
    [],
  );

  // Throttle stream display: show each unique text for 1s before next
  // const streamThrottleRef = useRef<
  //     Record<
  //         string,
  //         {
  //             queue: string[]; // incremental text chunks waiting to display
  //             revealed: string; // text currently revealed in bubble
  //             timer: ReturnType<typeof setTimeout> | null;
  //             ended: boolean;
  //         }
  //     >
  // >({});

  const flushPendingForRequestId = useCallback((requestId: string) => {
    const pending = pendingWsByRequestIdRef.current.get(requestId);
    if (!pending) return;
    pendingWsByRequestIdRef.current.delete(requestId);

    if (typeof pending.streamText === "string") {
      setChatHistory((prev) => {
        if (prev.some((m) => m.id === `${requestId}-publish`)) return prev;
        const idx = prev.findIndex(
          (m) => m.id === requestId && m.role === "ai",
        );
        if (idx !== -1) {
          return [
            ...prev.slice(0, idx),
            {
              ...prev[idx],
              text: pending.streamText ?? "",
              isStreaming: true,
              isEnding: !!pending.isEnd,
            },
            ...prev.slice(idx + 1),
          ];
        }
        return [
          ...prev,
          {
            role: "ai" as const,
            text: pending.streamText ?? "",
            id: requestId,
            isStreaming: true,
            isEnding: !!pending.isEnd,
          },
        ];
      });
    }

    if (pending.publishText) {
      setChatHistory((prev) => {
        const filtered = prev.filter(
          (m) => m.id !== requestId && m.id !== `${requestId}-publish`,
        );
        if (
          filtered.some(
            (m) => m.text === pending.publishText && m.role === "ai",
          )
        )
          return filtered;
        return [
          ...filtered,
          {
            role: "ai" as const,
            text: pending.publishText!,
            id: `${requestId}-publish`,
            isStreaming: false,
            apiPath: pending.apiPath,
          },
        ];
      });
      setIsTyping(false);
      ownedRequestsRef.current.delete(requestId);
    }
  }, []);

  // WebSocket Integration
  const token =
    typeof window !== "undefined" ? localStorage.getItem("auth_token") : null;

  // Extract userId from JWT token payload — this is the authoritative ID the server trusts.
  // Using it avoids 403s caused by mismatch between localStorage "user".id and token's userId.
  const getUserIdFromToken = (t: string | null): string | null => {
    if (!t) return null;
    try {
      const clean = t.trim().startsWith("Bearer ")
        ? t.trim().slice(7)
        : t.trim();
      const parts = clean.split(".");
      if (parts.length !== 3) return null;
      const payload = JSON.parse(atob(parts[1])) as Record<string, unknown>;
      return (payload.userId ??
        payload.sub ??
        payload.id ??
        payload.user_id) as string | null;
    } catch {
      return null;
    }
  };

  // Determine user ID for room subscription
  const getUserId = () => {
    // Priority: JWT token userId (matches what server authorizes) > localStorage user > prop
    const tokenId = getUserIdFromToken(token);
    if (tokenId) return tokenId;
    try {
      const userStr = localStorage.getItem("user");
      if (userStr) {
        const user = JSON.parse(userStr) as Record<string, unknown>;
        return (user.id || user.userId) as string | undefined;
      }
    } catch {
      // ignore
    }
    return userId;
  };

  const currentUserId = getUserId();
  const roomId = currentUserId ? `user:${currentUserId}` : null;

  useAeraWebSocket({
    roomId,
    token,
    enabled: !!roomId && !!token,
    onMessage: (msg) => {
      if (!msg) return;

      // Dedup: skip exact duplicate (React StrictMode can invoke handlers twice)
      const msgKey = `${msg.event}:${msg.data?.request_id}:${msg.data?.text}:${msg.data?.end}:${msg.data?.api_path}:${msg.data?.status}`;
      if (msgKey === lastProcessedMsgKeyRef.current) return;
      lastProcessedMsgKeyRef.current = msgKey;

      // /api/deploy_project succeeded → use deploy_id from WS to call /{deploy_id}/v1/init
      if (
        msg.event === "publish" &&
        msg.data?.api_path === "/api/deploy_project" &&
        msg.data?.status === "success" &&
        typeof msg.data?.deploy_id === "string" &&
        msg.data.deploy_id
      ) {
        runInitFromWsDeployId(msg.data.deploy_id as string);
      }

      if (
        msg.event === "publish" &&
        msg.data?.api_path &&
        typeof msg.data.api_path === "string" &&
        msg.data.api_path.includes("/v1/init")
      ) {
        if (msg.data.status === "success") {
          // Signal that the /v1/init request is done, so useSetup can start status polling
          setIsInitRequestDone(true);
        } else if (msg.data.status === "error") {
          console.error(
            "[AgentChatPanel] Setup error detected via WS:",
            msg.data.error || msg.data.message || "Unknown error",
          );
          triggerAutoRetry(
            msg.data.error || msg.data.message || "Unknown setup error",
          );
        }
      }

      if (msg.event === "publish" && msg.data) {
        const pubText =
          msg.data.responses?.[0]?.assistant_response ||
          msg.data.agent_response ||
          msg.data.text ||
          msg.data.response ||
          msg.data.content;
        const requestId = msg.data.request_id;

        // Queue if request_id not yet registered; will be flushed after HTTP response
        if (!requestId) return;
        if (!ownedRequestsRef.current.has(requestId)) {
          pendingWsByRequestIdRef.current.set(requestId, {
            ...(pendingWsByRequestIdRef.current.get(requestId) || {}),
            publishText:
              typeof pubText === "string"
                ? pubText
                : JSON.stringify(pubText ?? ""),
            apiPath: msg.data.api_path,
          });
          return;
        }

        if (pubText) {
          // "publish" event = final answer. Always replace stream bubble immediately.
          setChatHistory((prev) => {
            const filtered = prev.filter(
              (m) => m.id !== requestId && m.id !== `${requestId}-publish`,
            );
            if (filtered.some((m) => m.text === pubText && m.role === "ai"))
              return filtered;
            return [
              ...filtered,
              {
                role: "ai",
                text: pubText,
                id: requestId ? `${requestId}-publish` : undefined,
                isStreaming: false,
                apiPath: msg.data.api_path,
              },
            ];
          });
        }
        setIsTyping(false);
        ownedRequestsRef.current.delete(requestId); // cleanup after publish processed
      } else if (msg.event === "stream" && msg.data) {
        const socketText = msg.data.text || "";
        const requestId = msg.data.request_id;

        // Queue if request_id not yet registered; will be flushed after HTTP response
        if (!requestId) return;
        if (!ownedRequestsRef.current.has(requestId)) {
          const prevPending = pendingWsByRequestIdRef.current.get(requestId);
          pendingWsByRequestIdRef.current.set(requestId, {
            ...(prevPending || {}),
            streamText: (prevPending?.streamText ?? "") + socketText,
            isEnd: !!msg.data.end,
            apiPath: msg.data.api_path,
          });
          return;
        }

        // // Track accumulated revealed text per requestId
        // if (!streamThrottleRef.current[requestId]) {
        //     streamThrottleRef.current[requestId] = {
        //         queue: [],
        //         timer: null,
        //         ended: false,
        //         revealed: "",
        //     };
        // }
        // const tState = streamThrottleRef.current[requestId];

        // "stream" event = still in progress. Replace text (not accumulate) each chunk.
        // Even if socketText is empty, we register the stream to show the typing animation.
        if (socketText !== undefined) {
          setChatHistory((prev) => {
            if (prev.some((m) => m.id === `${requestId}-publish`)) return prev;
            const idx = prev.findIndex(
              (m) => m.id === requestId && m.role === "ai",
            );
            if (idx !== -1) {
              return [
                ...prev.slice(0, idx),
                {
                  ...prev[idx],
                  text: socketText,
                  isStreaming: true,
                  isEnding: !!msg.data.end,
                },
                ...prev.slice(idx + 1),
              ];
            }
            return [
              ...prev,
              {
                role: "ai" as const,
                text: socketText,
                id: requestId,
                isStreaming: true,
                isEnding: !!msg.data.end,
              },
            ];
          });

          setIsGreetingLoaded(false);
          setIsTyping(false);
        }
        // NOTE: No end detection here — we wait for "publish" event to signal completion
      }
    },
  });

  // When parent has confirmed deployId is resolved, use the prop directly to avoid a race where
  // localDeployId (useState) still holds the stale value while effectiveDeployId hasn't synced yet.
  const apiDeployId =
    deployIdResolved && deployId && deployId !== "temp-onboarding"
      ? deployId
      : effectiveDeployId || deployId;
  // Helper: check if deployId is a real deployment ID (not temp or empty)
  const isValidDeployId = apiDeployId && apiDeployId !== "temp-onboarding";

  // Determine which messages to display
  const displayMessages = chatHistory;

  // Fetch file list from business source API
  const fetchFileList = useCallback(async () => {
    // Block until parent has resolved the final deploy_id (prevents using stale deploy_id after reload)
    if (!deployIdResolved) return [];
    // Block business source until init/status reports success
    if (!isValidDeployId || (shouldShowSetup && !initWsSuccess)) {
      return [];
    }
    try {
      const token = localStorage.getItem("auth_token");
      // Priority: JWT token userId > localStorage user > prop (prevents 403 from user_id mismatch)
      let userIdParam = "";
      const tokenUid = getUserIdFromToken(token);
      if (tokenUid) {
        userIdParam = `?user_id=${tokenUid}`;
      } else {
        try {
          const userStr = localStorage.getItem("user");
          if (userStr) {
            const user = JSON.parse(userStr) as Record<string, unknown>;
            const uid = (user.id || user.userId) as string | undefined;
            if (uid) userIdParam = `?user_id=${uid}`;
          }
        } catch {
          // ignore
        }
        if (!userIdParam && userId) {
          userIdParam = `?user_id=${userId}`;
        }
      }

      const cleanDeployId = apiDeployId ? apiDeployId.trim() : "";

      const fileListUrl = `${process.env.NEXT_PUBLIC_PRODUCT_API_URL}/${cleanDeployId}/admin/business/source${userIdParam}${orderId ? `${userIdParam ? "&" : "?"}order_id=${orderId}` : ""}`;

      const headers: Record<string, string> = {
        "Content-Type": "application/json",
      };
      if (token) {
        const cleanToken = token.trim().startsWith("Bearer ")
          ? token.trim().substring(7)
          : token.trim();
        headers["Authorization"] = `Bearer ${cleanToken}`;
      }

      const response = await axios.post(
        "/api/proxy",
        {
          url: fileListUrl,
          method: "GET",
          headers,
        },
        {
          timeout: 120000,
        },
      );

      const files = response.data;
      setFileList(files);
      return files;
    } catch (error) {
      console.error("Error fetching file list:", error);
      if (axios.isAxiosError(error)) {
        console.error("Axios Error Details:", {
          status: error.response?.status,
          data: error.response?.data,
          url: `${process.env.NEXT_PUBLIC_PRODUCT_API_URL}/${apiDeployId}/admin/business/source`,
        });
      }
    }
    return [];
  }, [
    apiDeployId,
    isValidDeployId,
    userId,
    orderId,
    shouldShowSetup,
    initWsSuccess,
    deployIdResolved,
  ]);

  // Get file URL by filename
  const getFileUrl = useCallback(
    async (filename: string): Promise<string | null> => {
      // Check cache first
      let files = fileList;
      if (files.length === 0) {
        // Fetch if not cached
        files = await fetchFileList();
      }

      const file = files.find(
        (f: { filename?: string }) => f.filename === filename,
      );
      return file?.shared_link || null;
    },
    [fetchFileList, fileList],
  );

  // Prefetch file list when deployId becomes valid
  // Block until onboarding is COMPLETELY done (including success message)
  useEffect(() => {
    // Allow prefetch immediately when backend confirmed init success, even if we still show the 2s success UI.
    if (shouldShowSetup && !initWsSuccess) return;
    if (isValidDeployId) {
      fetchFileList();
    }
  }, [fetchFileList, isValidDeployId, shouldShowSetup, initWsSuccess]);

  // Fetch Greeting Message (Extracted for reuse)
  // Use a promise ref to deduplicate requests while ensuring all callers wait for result
  const greetingPromiseRef = useRef<Promise<void> | null>(null);

  const fetchGreeting = useCallback(async () => {
    if (greetingPromiseRef.current) return greetingPromiseRef.current;

    const fetchPromise = (async () => {
      setIsGreetingLoaded(true);
      try {
        const greetingUrl = `${process.env.NEXT_PUBLIC_PRODUCT_API_URL}/${apiDeployId}/v2/admin/greeting?language=ไทย&suggested_actions=${suggested_actions}`;
        const token =
          typeof window !== "undefined"
            ? localStorage.getItem("auth_token")
            : null;
        const headers: Record<string, string> = {
          "Content-Type": "application/json",
        };
        if (token) {
          headers["Authorization"] = `Bearer ${token}`;
        }

        const response = await axios.post(
          "/api/proxy",
          {
            url: greetingUrl,
            method: "POST",
            headers: headers,
          },
          { timeout: 120000 },
        );

        const data = response.data;

        // Register request_id so onMessage knows this belongs to this component
        if (data.request_id) {
          ownedRequestsRef.current.set(data.request_id, "greeting");
          flushPendingForRequestId(data.request_id);
        }
        const greetingText = data.response || data.text || data.agent_response;

        // If the response is just a queue acknowledgment, don't show it. The websocket will stream the real message.
        if (
          greetingText &&
          !greetingText.toLowerCase().includes("queued for processing")
        ) {
          const greetingMsg: ChatMessage = { role: "ai", text: greetingText };

          // Buffer greeting during onboarding while setup UI is still active
          const shouldBuffer = isOnboarding && shouldShowSetup;

          if (shouldBuffer) {
            setPendingGreeting(greetingMsg);
          } else {
            // Check if chat history already has messages (mutual exclusivity)
            setChatHistory((prev) => {
              // Prevent duplicate greeting or appending greeting if history already exists
              if (
                prev.length > 0 ||
                prev.some((msg) => msg.text === greetingText)
              )
                return prev;
              return [greetingMsg];
            });
            setHasNoHistory(false);
            setIsTyping(false);
          }
        } else if (
          greetingText?.toLowerCase().includes("queued for processing")
        ) {
          // Just wait for the stream, set typing indicator
          setHasNoHistory(false);
          setIsTyping(true);
        } else {
          // Only set empty if we really have nothing
          setChatHistory((prev) => (prev.length > 0 ? prev : []));
          if (chatHistory.length === 0) setHasNoHistory(true);

          setIsGreetingLoaded(true);
          setIsTyping(false);
        }
      } catch (error) {
        console.error("Error fetching agent greeting:", error);
        setIsTyping(false);
      }
    })();

    greetingPromiseRef.current = fetchPromise;

    try {
      await fetchPromise;
    } finally {
      greetingPromiseRef.current = null;
    }
  }, [
    apiDeployId,
    suggested_actions,
    isOnboarding,
    chatHistory.length,
    shouldShowSetup,
    flushPendingForRequestId,
  ]); // isOnboarding acts as a mode flag, unlikely to change mid-flow often, but safe to include

  // Load chat history
  const loadChatHistory = useCallback(async () => {
    if (!apiDeployId || !isValidDeployId) return;

    // Block until parent has resolved the final deploy_id (prevents stale deploy_id after page reload)
    if (!deployIdResolved) return;

    // Block until setup is COMPLETELY done (including success message)
    // But if backend already confirmed init success, start loading history immediately (avoid blank screen during 2s success UI).
    if (shouldShowSetup && !initWsSuccess) return;

    // Prevent duplicate calls
    if (isLoadingRef.current) return;

    // If we still can't resolve userId, don't fire a broken request; wait for next tick.
    // Priority: JWT token userId (authoritative) > currentUserId > prop > buyerId
    const tokenUid = getUserIdFromToken(localStorage.getItem("auth_token"));
    const resolvedUserId =
      tokenUid || currentUserId || userId || orderData?.buyerId;
    if (!resolvedUserId) return;

    isLoadingRef.current = true;
    setIsLoadingHistory(true);

    try {
      const historyUrl = `${process.env.NEXT_PUBLIC_PRODUCT_API_URL}/${apiDeployId}/admin/agent_chat?user_id=${resolvedUserId}${orderId ? `&order_id=${orderId}` : ""}`;

      const token =
        typeof window !== "undefined"
          ? localStorage.getItem("auth_token")
          : null;
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
      };
      if (token) {
        const cleanToken = token.trim().startsWith("Bearer ")
          ? token.trim().substring(7)
          : token.trim();
        headers["Authorization"] = `Bearer ${cleanToken}`;
      }

      const response = await axios.post(
        "/api/proxy",
        {
          url: historyUrl,
          method: "GET",
          headers: headers,
        },
        { timeout: 120000 },
      );

      const data = response.data;
      if (
        data.messages &&
        Array.isArray(data.messages) &&
        data.messages.length > 0
      ) {
        const sortedMessages = [...data.messages].sort(
          (a: { id?: number }, b: { id?: number }) => (a.id ?? 0) - (b.id ?? 0),
        );

        const loadedHistory: ChatMessage[] = sortedMessages.map(
          (msg: {
            role?: string;
            content?: string;
            text?: string;
            message?: string;
          }) => ({
            role: msg.role === "user" ? "user" : "ai",
            text: msg.content || msg.text || msg.message || "",
          }),
        );

        setChatHistory(loadedHistory);
        setHasNoHistory(false);
      } else {
        setHasNoHistory(true);
      }
    } catch (error) {
      console.error("Error loading agent chat history:", error);
      setHasNoHistory(true);
    } finally {
      setIsLoadingHistory(false);
      isLoadingRef.current = false;
    }
  }, [
    apiDeployId,
    isValidDeployId,
    deployIdResolved,
    shouldShowSetup,
    initWsSuccess,
    currentUserId,
    userId,
    orderId,
    orderData?.buyerId,
  ]);

  useEffect(() => {
    loadChatHistory();
  }, [loadChatHistory]);

  // When init/status becomes true: refresh the whole page once.
  // We use sessionStorage to prevent an infinite reload loop.
  useEffect(() => {
    if (!initWsSuccess) return;
    if (typeof window === "undefined") return;

    const deployKey = (effectiveDeployId || deployId || "").trim() || "unknown";
    const storageKey = `aera:init-success-reloaded:${deployKey}`;
    if (window.sessionStorage.getItem(storageKey) === "1") return;

    window.sessionStorage.setItem(storageKey, "1");
    window.location.reload();
  }, [initWsSuccess, effectiveDeployId, deployId]);

  // Watch for setup completion to trigger Greeting
  // Don't call greeting until: WS success received + success message shown for 2s + shouldShowSetup = false
  useEffect(() => {
    const currentStep = Number(localSetupStep);

    // Block ALL greeting calls while setup is in any phase
    // BUT allow it if initWsSuccess is true (backend confirmed finish)
    if (shouldShowSetup && !initWsSuccess) {
      return;
    }

    if (currentStep > 2 || (pendingGreeting && currentStep > 2)) {
      // 1. Check pending buffer
      if (pendingGreeting) {
        setChatHistory((prev) => {
          if (prev.some((msg) => msg.text === pendingGreeting.text))
            return prev;
          return [...prev, pendingGreeting];
        });
        setPendingGreeting(null);
        setHasNoHistory(false);
      }
      // 2. If no pending and history is empty, fetch greeting
      else if (
        chatHistory.length === 0 &&
        !isLoadingRef.current &&
        hasNoHistory
      ) {
        fetchGreeting();
      }
    }
  }, [
    localSetupStep,
    shouldShowSetup,
    isOnboarding,
    hadOnboarding,
    initWsSuccess,
    showSetupComplete,
    pendingGreeting,
    fetchGreeting,
    chatHistory.length,
    hasNoHistory,
  ]);

  const handleScroll = useCallback(() => {
    const container = chatScrollRef.current;
    if (!container) return;
    onContentScrollStateChange?.(container.scrollTop > 0);
    const distanceFromBottom =
      container.scrollHeight - container.scrollTop - container.clientHeight;
    setShowScrollButton(distanceFromBottom > 150);
  }, [onContentScrollStateChange]);

  const scrollToBottom = useCallback(() => {
    const container = chatScrollRef.current;
    if (container)
      container.scrollTo({ top: container.scrollHeight, behavior: "smooth" });
  }, []);

  // Auto-scroll: always jump to bottom on initial load; smart 150px threshold after
  useEffect(() => {
    const raf = requestAnimationFrame(() => {
      if (!chatHistory.length) return;
      const container = chatScrollRef.current;
      if (!container) return;
      if (!hasInitialScrolled.current) {
        container.scrollTop = container.scrollHeight;
        hasInitialScrolled.current = true;
        return;
      }
      const distanceFromBottom =
        container.scrollHeight - container.scrollTop - container.clientHeight;
      if (distanceFromBottom <= 150) {
        container.scrollTop = container.scrollHeight;
      }
    });
    return () => cancelAnimationFrame(raf);
  }, [chatHistory, isTyping, orderData?.buyerId]);

  // File Upload Logic
  const [isUploading, setIsUploading] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  // Preview logic lifted to parent
  // const [previewFile, setPreviewFile] = useState<File | { name: string; url: string; type: string; size?: number } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const allowedExtensions = [
        ".xlsx",
        ".htm",
        ".csv",
        ".gif",
        ".pdf",
        ".png",
        ".jpeg",
        ".html",
        ".docx",
        ".json",
        ".jpg",
        ".bmp",
        ".txt",
      ];
      const newFiles = Array.from(e.target.files);

      // Filter files by allowed extensions
      const validFiles = newFiles.filter((file) => {
        const ext = "." + file.name.split(".").pop()?.toLowerCase();
        return allowedExtensions.includes(ext);
      });

      const rejectedFiles = newFiles.filter((file) => {
        const ext = "." + file.name.split(".").pop()?.toLowerCase();
        return !allowedExtensions.includes(ext);
      });

      if (rejectedFiles.length > 0) {
        const rejectedNames = rejectedFiles.map((f) => f.name).join(", ");
        alert(
          `ไฟล์ต่อไปนี้ไม่รองรับ: ${rejectedNames}\n\nไฟล์ที่รองรับ: ${allowedExtensions.join(", ")}`,
        );
      }

      if (validFiles.length > 0) {
        setSelectedFiles((prev) => [...prev, ...validFiles]);
      }
    }
    if (e.target) e.target.value = "";
  };

  const uploadFile = async (file: File): Promise<string> => {
    // Block upload until init/status reports success
    if (!isValidDeployId || (shouldShowSetup && !initWsSuccess)) {
      throw new Error(
        "ระบบกำลังติดตั้งอยู่หรือยังไม่ได้พร้อมใช้งาน กรุณารอสักครู่ก่อนอัปโหลดไฟล์",
      );
    }
    const formData = new FormData();
    formData.append("file", file);

    const token = localStorage.getItem("auth_token");

    // Get userId to append to URL
    let userIdParam = "";

    // Priority: LocalStorage (Auth Source of Truth) > Prop
    try {
      const userStr = localStorage.getItem("user");
      if (userStr) {
        const user = JSON.parse(userStr);
        const uid = user.id || user.userId;
        if (uid) userIdParam = `?user_id=${uid}`;
      }
    } catch {
      // ignore
    }

    // Fallback to prop if not found in localStorage
    if (!userIdParam && userId) {
      userIdParam = `?user_id=${userId}`;
    }

    const uploadUrl = `${process.env.NEXT_PUBLIC_PRODUCT_API_URL}/${apiDeployId}/admin/business/source${userIdParam}`;

    const headers: Record<string, string> = {};
    if (token) {
      const cleanToken = token.trim().startsWith("Bearer ")
        ? token.trim().substring(7)
        : token.trim();
      headers["Authorization"] = `Bearer ${cleanToken}`;
    }

    // Create specialized FormData for our proxy-upload endpoint
    const proxyFormData = new FormData();
    proxyFormData.append("file", file);
    proxyFormData.append("url", uploadUrl);
    proxyFormData.append("headers", JSON.stringify(headers));

    const response = await fetch("/api/proxy-upload", {
      method: "POST",
      body: proxyFormData,
      // Fetch automatically sets Content-Type for FormData
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error("Upload failed:", {
        status: response.status,
        statusText: response.statusText,
        error: errorData,
      });
      throw new Error(
        `Upload failed: ${response.status} - ${errorData.detail || errorData.error || response.statusText}`,
      );
    }

    const data = await response.json();
    const uploadedFilename = data.filename || file.name;

    // No optimistic update here. We will fetch the full list after all uploads are done.
    return uploadedFilename;
  };

  const handlePreviewFile = (
    file: File | { name: string; url: string; type: string; size?: number },
  ) => {
    if (typeof window === "undefined") return;

    // postMessage cannot reliably pass File blobs to the parent modal.
    // Always send a serializable payload with a blob URL for local files.
    const previewFile =
      typeof file === "object" &&
      file !== null &&
      "url" in file &&
      typeof file.url === "string" &&
      file.url.length > 0
        ? {
            name: file.name,
            url: file.url,
            type: file.type || "",
            size: file.size,
          }
        : {
            name: file.name || "preview",
            url: URL.createObjectURL(file as Blob),
            type: file.type || "",
            size: file.size,
          };

    window.parent.postMessage(
      {
        type: "OPEN_FILE_PREVIEW_MODAL",
        props: {
          file: previewFile,
        },
      },
      "*",
    );
  };

  const handleSendMessage = async (messageText?: string) => {
    const textContent = messageText || inputValue.trim();

    // We proceed if there is text OR files selected
    if (!textContent && selectedFiles.length === 0) return;

    // Block chat API while init/status is still false
    if (shouldShowSetup && !initWsSuccess) {
      setChatHistory((prev) => {
        const last = prev[prev.length - 1];
        const warning =
          "ระบบกำลังติดตั้งอยู่ กรุณารอสักครู่จนกว่าการติดตั้งจะเสร็จสิ้นก่อนเริ่มใช้งานแชท";
        if (last && last.role === "ai" && last.text === warning) return prev;
        return [...prev, { role: "ai" as const, text: warning }];
      });
      return;
    }

    setIsTyping(true);
    if (selectedFiles.length > 0) setIsUploading(true);

    let finalMessageText = textContent;
    const uploadedFiles: string[] = [];
    const failedFiles: string[] = [];

    try {
      // 1. Handle File Uploads if present
      if (selectedFiles.length > 0) {
        // Upload sequentially
        for (const file of selectedFiles) {
          try {
            const filename = await uploadFile(file);
            // Wrap filename with brackets
            uploadedFiles.push(`[${filename}]`);
          } catch (uploadError) {
            console.error(`Failed to upload ${file.name}`, uploadError);
            failedFiles.push(file.name);
          }
        }

        // Construct message about uploads
        if (uploadedFiles.length > 0) {
          const uploadMsg = uploadedFiles.join(" ");
          if (!finalMessageText) {
            finalMessageText = uploadMsg;
          } else {
            finalMessageText = `${finalMessageText} ${uploadMsg}`;
          }

          // CRITICAL: Fetch file list to get shared_links for the new files
          // This ensures InlineAttachment can find the URL in the cache
          await fetchFileList();
        }

        if (failedFiles.length > 0) {
          setChatHistory((prev) => [
            ...prev,
            {
              role: "ai",
              text: `Failed to upload: ${failedFiles.join(", ")}. Please try again.`,
            },
          ]);
        }
      }

      // 2. Only proceed if we have text or successfully uploaded files
      if (finalMessageText || uploadedFiles.length > 0) {
        // Resolve user id — JWT token userId is authoritative (matches server auth)
        const tokenUid = getUserIdFromToken(localStorage.getItem("auth_token"));
        const resolvedUserId =
          tokenUid || currentUserId || userId || orderData?.buyerId;
        if (!resolvedUserId) {
          setChatHistory((prev) => [
            ...prev,
            {
              role: "ai",
              text: "ไม่สามารถระบุผู้ใช้สำหรับห้องแชทได้ กรุณาลองรีเฟรชหน้าแล้วเข้าสู่ระบบใหม่อีกครั้ง",
            },
          ]);
          setIsTyping(false);
          return;
        }

        // Add user message
        const newHistory: ChatMessage[] = [
          ...chatHistory,
          { role: "user", text: finalMessageText },
        ];
        setChatHistory(newHistory);
        requestAnimationFrame(() => scrollToBottom());
        setInputValue("");
        setSelectedFiles([]); // Clear files after attempt
        setHasNoHistory(false);

        // 3. Call Chat API
        const chatUrl = `${process.env.NEXT_PUBLIC_PRODUCT_API_URL}/${apiDeployId}/admin/agent_chat?user_id=${resolvedUserId}`;

        const token =
          typeof window !== "undefined"
            ? localStorage.getItem("auth_token")
            : null;
        const headers: Record<string, string> = {
          "Content-Type": "application/json",
        };

        if (token) {
          const cleanToken = token.trim().startsWith("Bearer ")
            ? token.trim().substring(7)
            : token.trim();
          headers["Authorization"] = `Bearer ${cleanToken}`;
        }

        const response = await axios.post(
          "/api/proxy",
          {
            url: chatUrl,
            method: "POST",
            headers: headers,
            data: {
              message: finalMessageText,
              suggested_actions: suggested_actions,
              user_id: resolvedUserId,
              ...(orderId ? { order_id: orderId } : {}),
            },
          },
          {
            timeout: 120000, // 120 seconds
          },
        );

        const data = response.data;
        // The actual AI response will arrive via WebSocket stream — don't show anything here.
        if (data.status === "accepted") {
          // Register request_id so onMessage knows this belongs to this component
          if (data.request_id) {
            ownedRequestsRef.current.set(data.request_id, "agent_chat");
            flushPendingForRequestId(data.request_id);
          }
          // waiting for WebSocket...
        } else {
          const aiResponse = data.agent_response || data.response || data.text;
          if (aiResponse) {
            setChatHistory((prev) => [
              ...prev,
              { role: "ai", text: aiResponse },
            ]);
          }
        }
      } else if (failedFiles.length > 0 && !finalMessageText) {
        // If all failed and no text, we already showed error message
      }
    } catch (error) {
      console.error("Error calling agent chat API:", error);
      let errorMessage = "Error: Could not connect to agent.";
      if (axios.isAxiosError(error)) {
        const errorData = error.response?.data || {};
        errorMessage =
          errorData.error || errorData.message || error.message || errorMessage;
      }
      setChatHistory((prev) => [
        ...prev,
        {
          role: "ai",
          text: errorMessage,
        },
      ]);
      setIsTyping(false); // error path: no WS coming, clear immediately
    } finally {
      setIsUploading(false);
    }
  };

  const chatInputLocked =
    isTyping ||
    isUploading ||
    isLoadingHistory ||
    !!isGreetingLoaded ||
    !!(isOnboarding && !isValidDeployId && chatHistory.length === 0) ||
    (shouldShowSetup && setupStatusText === "✅ ติดตั้งสำเร็จแล้ว");

  useEffect(() => {
    const el = chatSectionRef.current;
    if (!el) return;

    const updateWidth = () => {
      setSectionWidth(el.getBoundingClientRect().width);
    };

    updateWidth();

    const observer = new ResizeObserver(updateWidth);
    observer.observe(el);
    const parent = el.parentElement;
    if (parent) observer.observe(parent);

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const el = chatSectionRef.current;
    if (!el) return;
    setSectionWidth(el.getBoundingClientRect().width);
  }, [layoutResizeKey]);

  return (
    <>
      <div
        ref={chatSectionRef}
        className="flex h-full min-h-0 w-full min-w-0 flex-col"
        data-section-width={sectionWidth}
      >
        {/* Chat Area - Matched ChatPanel styling */}
        <div className="flex-1 relative overflow-hidden">
          <div
            ref={chatScrollRef}
            className="absolute inset-0 p-4 overflow-y-auto space-y-4"
            onScroll={handleScroll}
          >
            <>
              {displayMessages.map((msg, i) => {
                const { text, attachments, suggestSection, hasLoadingSpinner } =
                  parseMessage(msg.text);
                const isUser = msg.role === "user";

                return (
                  <div key={i}>
                    <div
                      className={`flex ${isUser ? "justify-end" : "justify-start"} animate-in slide-in-from-bottom-2 duration-300`}
                    >
                      <div
                        className={`flex flex-col max-w-[80%] ${isUser ? "items-end" : "items-start"} ${!isUser && msg.isEnding ? "animate-pulse" : ""}`}
                      >
                        {/* Attachments - Outside Bubble (Top) */}
                        {attachments && (
                          <div
                            className={`mb-1 flex flex-col ${isUser ? "items-end text-right" : "items-start text-left"} w-full`}
                          >
                            {/* <div className={`${isUser ? "text-right" : "text-left"}`}> */}
                            {/* Render attachments in order: Images → Files → Text */}
                            {(() => {
                              const lines = attachments.split("\n");
                              const imageItems: {
                                line: string;
                                idx: number;
                                filename: string;
                                fileExt: string;
                              }[] = [];
                              const fileItems: {
                                line: string;
                                idx: number;
                                filename: string;
                                fileExt: string;
                                isDocument: boolean;
                              }[] = [];
                              const textItems: { line: string; idx: number }[] =
                                [];

                              // Categorize each line
                              lines.forEach((line, idx) => {
                                const bracketMatch =
                                  line.match(/^\[([^\[\]]+)\]$/);
                                if (bracketMatch) {
                                  const filename = bracketMatch[1];
                                  const fileExt =
                                    filename.split(".").pop()?.toUpperCase() ||
                                    "FILE";
                                  const isImage = [
                                    "JPG",
                                    "JPEG",
                                    "PNG",
                                    "GIF",
                                    "BMP",
                                  ].includes(fileExt);
                                  const isDocument = [
                                    "PDF",
                                    "DOC",
                                    "DOCX",
                                    "XLS",
                                    "XLSX",
                                    "PPT",
                                    "PPTX",
                                    "TXT",
                                    "CSV",
                                  ].includes(fileExt);
                                  if (isImage) {
                                    imageItems.push({
                                      line,
                                      idx,
                                      filename,
                                      fileExt,
                                    });
                                  } else {
                                    fileItems.push({
                                      line,
                                      idx,
                                      filename,
                                      fileExt,
                                      isDocument,
                                    });
                                  }
                                } else {
                                  textItems.push({ line, idx });
                                }
                              });

                              return (
                                <>
                                  {/* 1. Images first */}
                                  {imageItems.map((item) => (
                                    <InlineAttachment
                                      key={`img-${item.idx}`}
                                      filename={item.filename}
                                      getFileUrl={getFileUrl}
                                      handlePreviewFile={handlePreviewFile}
                                    />
                                  ))}

                                  {/* 2. File cards */}
                                  {fileItems.map((item) => {
                                    const handleFileClick = async () => {
                                      const url = await getFileUrl(
                                        item.filename,
                                      );
                                      if (!url) {
                                        console.error(
                                          "File URL not found for:",
                                          item.filename,
                                        );
                                        return;
                                      }
                                      handlePreviewFile({
                                        name: item.filename,
                                        url: url,
                                        type: item.fileExt.toLowerCase(),
                                        size: 0,
                                      });
                                    };
                                    return (
                                      <div
                                        key={`file-${item.idx}`}
                                        className="flex items-center gap-3 p-3 bg-aera-white border border-gray-200 rounded-xl my-2 max-w-sm shadow-sm text-left cursor-pointer hover:bg-gray-50 transition-colors"
                                        onClick={handleFileClick}
                                      >
                                        <div
                                          className={`p-2 rounded-lg ${item.isDocument ? "bg-aera-redlight text-aera-red" : "bg-gray-50 text-aera-charcoal"}`}
                                        >
                                          <Icon name="file" size={20} />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                          <div className="font-medium text-aera-charcoal truncate text-sm">
                                            {item.filename}
                                          </div>
                                          <div className="text-xs text-aera-charcoal">
                                            {item.fileExt}
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  })}

                                  {/* 3. Text / Markdown last */}
                                  {textItems.map((item) => (
                                    <ReactMarkdown
                                      key={`text-${item.idx}`}
                                      remarkPlugins={[remarkGfm, remarkBreaks]}
                                      components={{
                                        a: ({ ...props }) => {
                                          const content = String(
                                            props.children,
                                          );
                                          const href = props.href || "";
                                          const isFile =
                                            href.match(
                                              /\.(pdf|doc|docx|xls|xlsx|ppt|pptx|txt|csv|jpg|jpeg|png|gif|webp)$/i,
                                            ) ||
                                            href.includes(
                                              "/uploads/documents/",
                                            );

                                          if (isFile) {
                                            const fileName = content;
                                            let cleanHref = href;
                                            try {
                                              cleanHref =
                                                decodeURIComponent(href);
                                            } catch {
                                              // ignore
                                            }
                                            const fileType =
                                              cleanHref
                                                .split("?")[0]
                                                .split(".")
                                                .pop()
                                                ?.toUpperCase() || "FILE";
                                            const isImage = [
                                              "JPG",
                                              "JPEG",
                                              "PNG",
                                              "GIF",
                                              "WEBP",
                                              "SVG",
                                              "BMP",
                                            ].includes(fileType);

                                            if (isImage) {
                                              return (
                                                <AeraImage
                                                  native
                                                  src={href}
                                                  alt={fileName}
                                                  className="max-w-full rounded-lg my-2 cursor-pointer hover:opacity-90 transition-opacity border border-gray-200"
                                                  style={{ maxHeight: "300px" }}
                                                  onClick={() =>
                                                    handlePreviewFile({
                                                      name: fileName,
                                                      url: href,
                                                      type: fileType.toLowerCase(),
                                                      size: 0,
                                                    })
                                                  }
                                                />
                                              );
                                            }

                                            return (
                                              <div
                                                className="flex items-center gap-3 p-3 bg-aera-white border border-gray-200 rounded-xl my-2 cursor-pointer hover:bg-gray-50 transition-colors max-w-sm group shadow-sm text-left"
                                                onClick={() =>
                                                  handlePreviewFile({
                                                    name: fileName,
                                                    url: href,
                                                    type: fileType.toLowerCase(),
                                                    size: 0,
                                                  })
                                                }
                                              >
                                                <div className="bg-aera-redlight p-2 rounded-lg text-aera-red">
                                                  <Icon name="file" size={20} />
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                  <div className="font-medium text-aera-charcoal truncate text-sm">
                                                    {fileName}
                                                  </div>
                                                  <div className="text-xs text-aera-charcoal">
                                                    {fileType}
                                                  </div>
                                                </div>
                                              </div>
                                            );
                                          }

                                          const prefix = `${process.env.NEXT_PUBLIC_API_BASE_URL}/uploads/documents/`;
                                          const displayText =
                                            content.startsWith(prefix)
                                              ? content.replace(prefix, "")
                                              : content;

                                          return (
                                            <AeraButton
                                              as="a"
                                              {...props}
                                              variant="link"
                                              textColor="amberdark"
                                              target="_blank"
                                              rel="noopener noreferrer"
                                            >
                                              {displayText}
                                            </AeraButton>
                                          );
                                        },
                                      }}
                                    >
                                      {item.line}
                                    </ReactMarkdown>
                                  ))}
                                </>
                              );
                            })()}
                            {/* </div> */}
                          </div>
                        )}

                        {/* Text Bubble (Bottom) */}
                        {(text || msg.isStreaming) && (
                          <div
                            className={`px-3 opacity-90 transition-opacity rounded-lg text-sm ${
                              isUser
                                ? "bg-aera-lightgray text-aera-charcoal rounded-br-none py-1"
                                : `bg-aera-white text-aera-charcoal border rounded-bl-none py-3 border-aera-lightgray`
                            }
                                                            }`}
                            style={{
                              overflowWrap: "break-word",
                              wordBreak: "break-word",
                            }}
                          >
                            {msg.role === "ai" && (
                              <div className="text-xs font-semibold text-aera-charcoal mb-2 flex items-center gap-2">
                                <span>{workerName}</span>
                              </div>
                            )}
                            {msg.role === "ai" ? (
                              <>
                                <div
                                  className="prose prose-sm max-w-none prose-slate
                                prose-headings:text-aera-charcoal prose-headings:font-bold prose-headings:mb-1 prose-headings:mt-2
                                prose-p:leading-relaxed prose-p:my-1
                                prose-strong:text-aera-charcoal prose-strong:font-bold
                                prose-ul:list-disc prose-ul:pl-4 prose-ul:my-1
                                prose-ol:list-decimal prose-ol:pl-4 prose-ol:my-1
                                prose-li:my-0.5
                                prose-code:bg-aera-white prose-code:px-1 prose-code:rounded prose-code:text-aera-amber
                                prose-pre:bg-aera-charcoal prose-pre:text-aera-white prose-pre:p-4 prose-pre:rounded-lg prose-pre:overflow-x-auto
                                wrap-break-words"
                                >
                                  <div
                                    className={`${hasLoadingSpinner ? "flex" : "grid"} items-center gap-2`}
                                  >
                                    {hasLoadingSpinner && (
                                      <div className="flex">
                                        <div
                                          className="text-aera-amber animate-spin"
                                          style={{ animationDuration: "2s" }}
                                        >
                                          <Icon name="hourglass" size={18} />
                                        </div>
                                      </div>
                                    )}
                                    <ReactMarkdown
                                      remarkPlugins={[remarkGfm, remarkBreaks]}
                                      components={markdownComponents}
                                    >
                                      {msg.isStreaming
                                        ? `${text} ![cursor](cursor)`
                                        : text}
                                    </ReactMarkdown>
                                    {hasLoadingSpinner && (
                                      <div className="flex items-center gap-1 w-max">
                                        <div
                                          className="w-2 h-2 bg-aera-amber rounded-full animate-bounce"
                                          style={{
                                            animationDelay: "0ms",
                                            animationDuration: "1s",
                                          }}
                                        ></div>
                                        <div
                                          className="w-2 h-2 bg-aera-amber rounded-full animate-bounce"
                                          style={{
                                            animationDelay: "150ms",
                                            animationDuration: "1s",
                                          }}
                                        ></div>
                                        <div
                                          className="w-2 h-2 bg-aera-amber rounded-full animate-bounce"
                                          style={{
                                            animationDelay: "300ms",
                                            animationDuration: "1s",
                                          }}
                                        ></div>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </>
                            ) : (
                              <div className="prose prose-sm max-w-none prose-invert text-aera-charcoal prose-a:text-aera-charcoal prose-p:my-0 prose-pre:my-0">
                                <ReactMarkdown
                                  remarkPlugins={[remarkGfm, remarkBreaks]}
                                  components={{
                                    a: ({ ...props }) => (
                                      <AeraButton
                                        as="a"
                                        {...props}
                                        variant="link"
                                        textColor="amberdark"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                      />
                                    ),
                                  }}
                                >
                                  {text}
                                </ReactMarkdown>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Suggested Actions - Below Text Bubble */}
                        <SuggestedActions
                          suggestSection={suggestSection}
                          isUser={isUser}
                          disabled={isTyping}
                          onSelect={(label) => handleSendMessage(label)}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}

              {(isTyping ||
                isGreetingLoaded ||
                (shouldShowSetup && effectiveSetupStatusText)) && (
                <ProcessingIndicator
                  workerName={workerName}
                  processingMessage={
                    shouldShowSetup ? effectiveSetupStatusText : undefined
                  }
                />
              )}
            </>

            <div ref={chatEndRef} />
          </div>
          {/* Scroll to bottom button */}
          {showScrollButton && (
            <ScrollToBottomButton onClick={scrollToBottom} />
          )}
        </div>

        {/* Input Area - Matched ChatPanel styling */}
        <div className="p-4">
          <SelectedFilesStrip
            files={selectedFiles}
            sectionWidth={sectionWidth}
            onPreview={handlePreviewFile}
            onRemove={(index) =>
              setSelectedFiles((prev) => prev.filter((_, i) => i !== index))
            }
          />

          <ChatInput
            textareaRef={textareaRef}
            value={inputValue}
            onChange={setInputValue}
            onSend={() => handleSendMessage()}
            placeholder={
              selectedFiles.length > 0
                ? `ส่ง ${selectedFiles.length} ไฟล์พร้อมข้อความ...`
                : `ส่งข้อความให้ ${workerName}...`
            }
            disabled={chatInputLocked}
            sendDisabled={
              (!inputValue.trim() && selectedFiles.length === 0) ||
              chatInputLocked
            }
            sendHighlighted={
              Boolean(inputValue.trim()) || selectedFiles.length > 0
            }
            isBusy={isUploading}
            fileInputRef={fileInputRef}
            onFileChange={handleFileSelect}
            attachDisabled={chatInputLocked}
            attachBusy={isUploading}
          />

          {!deployId && (
            <p className="text-xs text-aera-red mt-2 text-center">
              อุปกรณ์ AI ยังไม่ถูกตั้งค่า กรุณาตั้งค่าก่อนใช้งาน
            </p>
          )}
        </div>
      </div>
    </>
  );
}
